import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import usdaLogoDiagram from '../../../assets/images/usda-logo-black.png';
import NorthArrow from '../../../assets/images/north-arrow.png';
import { PrintConstructionReportsVM } from './PrintConstructionReportsVM';
import { formatMonthDayYear, getMonthDayYear } from 'cd-eft/utils/formatDate';
import './PrintConstructionReports.scss';
import { WDTConstructionReport, splitTable } from 'cd-eft/components/Reports/WDTConstructionReport/WDTConstructionReport';
import { TerraceConstructionReport } from 'cd-eft/components/Reports/TerraceConstructionReport/TerraceConstructionReport';
import { CombinedTerraceConstructionReport } from 'cd-eft/components/Reports/CombinedTerraceConstructionReport/CombinedTerraceConstructionReport';

interface IPrintConstructionReportsProps {
  saveButtonPopup: boolean;
  togglePrintReportPopup: () => void;
  onClick?: () => void; // Add this
  project: any;
  printButtonClicked: boolean;
}

export const PrintDesignComponent = (props: IPrintConstructionReportsProps) => {
  const vm = useRef(new PrintConstructionReportsVM(props)).current;

  useEffect(() => {
    if (props.printButtonClicked) {
      vm.generateConstructionReport();
    }
  }, [props.printButtonClicked]);

  return (
    <div className="print-report">
      {/*Rectangular Pit Structure Report*/}
      {vm.dataForDesignElement11.length > 0 &&
        vm.dataForDesignElement11.map((item, index) => (
          <PrintReportElementContainer
            key={`rectangular-pit-${index}`}
            // pageNumber={index}
            item={item}
            project={props.project}
          // totalPages={vm.totalPages}
          >
            <table className="report-structure-property">
              <thead>
                <tr>
                  <td className="underline" colSpan={3}>
                    {item.type} Properties
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Bottom Width(BW) = {item.width} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Bottom Length (BL) = {item.length} ft</td>
                </tr>
              </thead>
              <tbody>
                {/* Mapping over slopes */}
                {item.slopes &&
                  JSON.parse(item.slopes).map((slope) => (
                    <tr key={slope.slopeName}>
                      <td>
                        {slope.slopeName} = {slope.slopeValue}
                      </td>
                      <td></td>
                    </tr>
                  ))}

                <tr>
                  <td colSpan={3}>Bottom Elevation = {JSON.parse(item.center).z} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Rotation Azimuth = {item.orienAzimuth} degrees</td>
                </tr>
                <tr>
                  <td className="underline" colSpan={3}>
                    Design Results
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Cut Volume (CY) = {item.cutVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Storage Volume (Ac-Ft) = {item.storageVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Storage Depth (ft) = {item.storageDepth.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </PrintReportElementContainer>
        ))}

      {/* Round Pit Report */}
      {vm.dataForDesignElement12.length > 0 &&
        vm.dataForDesignElement12.map((item, index) => (
          <PrintReportElementContainer
            key={`round-pit-${index}`}
            // pageNumber={vm.dataForDesignElement11.length + index}
            item={item}
            project={props.project}
          // totalPages={vm.totalPages}
          >
            <table className="report-structure-property">
              <thead>
                <tr>
                  <td className="underline" colSpan={3}>
                    {item.type} Properties
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Radius (R) = {item.radius} ft</td>
                </tr>
                {/* Mapping over slope */}
                <tr>
                  <td>Side Slope = {JSON.parse(item.slope).slopeValue}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={3}>Bottom Elevation = {JSON.parse(item.center).z} ft</td>
                </tr>
                <tr>
                  <td className="underline" colSpan={3}>
                    Design Results
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Cut Volume (CY) = {item.cutVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Storage Volume (Ac-Ft) = {item.storageVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Storage Depth (ft) = {item.storageDepth.toFixed(2)}</td>
                </tr>
              </thead>
            </table>
          </PrintReportElementContainer>
        ))}

      {/* Diked Pit Structure Report */}
      {vm.dataForDesignElement13.length > 0 &&
        vm.dataForDesignElement13.map((item, index) => (
          <PrintReportElementContainer
            key={`diked-pit-${index}`}
            // pageNumber={vm.dataForDesignElement11.length + vm.dataForDesignElement12.length + index}
            item={item}
            project={props.project}
          // totalPages={vm.totalPages}
          >
            <table className="report-structure-property diked-pit-table">
              <thead>
                <tr>
                  <td className="underline" colSpan={3}>
                    {item.type} Properties
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Bottom Width(BW) = {item.width} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Bottom Length (BL) = {item.length} ft</td>
                </tr>
                {Array(
                  Math.max(
                    JSON.parse(item.sideSlopes).length,
                    JSON.parse(item.fillSlopes).length + JSON.parse(item.cutSlopes).length
                  )
                )
                  .fill(null)
                  .map((_, index) => (
                    <tr key={`diked-pit-tr-${index}`}>
                      {/* Displaying sideSlopes on the left */}
                      <td>
                        {JSON.parse(item.sideSlopes)[index] &&
                          `${JSON.parse(item.sideSlopes)[index].slopeName} = ${JSON.parse(item.sideSlopes)[index].slopeValue
                          }`}
                      </td>

                      {/* Displaying fillSlopes and cutSlopes on the right */}
                      <td>
                        {(
                          index % 2 === 0
                            ? JSON.parse(item.fillSlopes)[index / 2]
                            : JSON.parse(item.cutSlopes)[Math.floor(index / 2)]
                        )
                          ? `${(index % 2 === 0
                            ? JSON.parse(item.fillSlopes)[index / 2]
                            : JSON.parse(item.cutSlopes)[Math.floor(index / 2)]
                          ).slopeName
                          } = ${(index % 2 === 0
                            ? JSON.parse(item.fillSlopes)[index / 2]
                            : JSON.parse(item.cutSlopes)[Math.floor(index / 2)]
                          ).slopeValue
                          }`
                          : ''}
                      </td>
                    </tr>
                  ))}
                <tr>
                  <td colSpan={3}>Bottom Elevation = {JSON.parse(item.center).z.toFixed(2)} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Top Elevation = {JSON.parse(item.padVertices)[0][0].z} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Top Width(TW) = {item.topWidth} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Rotation Azimuth = {item.orienAzimuth} degrees</td>
                </tr>
                <tr>
                  <td className="underline" colSpan={3}>
                    Design Results
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Cut Volume (CY) = {item.cutVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Fill Volume (CY) = {item.fillVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Storage Volume (Ac-Ft) = {item.storageVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Storage Depth (ft) = {item.storageDepth.toFixed(2) || 'N/A'}</td>
                </tr>
              </thead>
            </table>
          </PrintReportElementContainer>
        ))}

      {/*Pad Structure Report*/}
      {vm.dataForDesignElement14.length > 0 &&
        vm.dataForDesignElement14.map((item, index) => (
          <PrintReportElementContainer key={`pad-${index}`} item={item} project={props.project}>
            <table className="report-structure-property">
              <thead>
                <tr>
                  <td className="underline" colSpan={3}>
                    {item.type} Properties
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Pad Width(PW) = {item.width} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Pad Length (PL) = {item.length} ft</td>
                </tr>
              </thead>
              <tbody>
                {Array(Math.max(JSON.parse(item.fillSlopes).length + JSON.parse(item.cutSlopes).length))
                  .fill(null)
                  .map((_, index) => (
                    <tr key={`pad-tr-${index}`}>
                      {/* Displaying fillSlopes and cutSlopes */}
                      <td>
                        {(
                          index % 2 === 0
                            ? JSON.parse(item.fillSlopes)[index / 2]
                            : JSON.parse(item.cutSlopes)[Math.floor(index / 2)]
                        )
                          ? `${(index % 2 === 0
                            ? JSON.parse(item.fillSlopes)[index / 2]
                            : JSON.parse(item.cutSlopes)[Math.floor(index / 2)]
                          ).slopeName
                          } = ${(index % 2 === 0
                            ? JSON.parse(item.fillSlopes)[index / 2]
                            : JSON.parse(item.cutSlopes)[Math.floor(index / 2)]
                          ).slopeValue
                          }`
                          : ''}
                      </td>
                    </tr>
                  ))}

                <tr>
                  <td colSpan={3}>Pad Elevation = {JSON.parse(item.center).z} ft</td>
                </tr>
                <tr>
                  <td colSpan={3}>Rotation Azimuth = {item.orienAzimuth} degrees</td>
                </tr>

                <tr>
                  <td className="underline" colSpan={3}>
                    Design Results
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}>Cut Volume (CY) = {item.cutVolume.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Fill Volume (CY) = {item.fillVolume.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </PrintReportElementContainer>
        ))}

      {/*Land Leveling Structure Report*/}
      {vm.dataForDesignElement15.length > 0 &&
        vm.dataForDesignElement15.map((item, index) => (
          <PrintReportElementContainer key={`ll-${index}`} item={item} project={props.project}>
            <div className="px-2">
              <div className="pb-2">
                <u>{item.type} Properties</u>
                <div>Land Leveling Elevation = {JSON.parse(item.center).z} ft</div>
              </div>

              <div className="pb-2">
                <u>Base Point</u>
                <div>X = {JSON.parse(item.center).x}</div>
                <div>Y = {JSON.parse(item.center).y}</div>
                <div>Z = {JSON.parse(item.center).z}</div>
              </div>

              <div className="pb-2">
                <u>Percent Slopes</u>
                <div>Sx = {item.xslope}</div>
                <div>Sy = {item.yslope}</div>
                <div>Borrow = {item.borrow}</div>
                <div>Waste = {item.waste}</div>
                <div>Cut/Fill Ratio = {item.cutFillRatio}</div>
                <div>Grid spacing (ft) = {item.llStakeoutGrid}</div>
                <div>Buffer Width(ft) = {item.bufferSize}</div>
              </div>

              <div className="pb-2">
                <u>Land Level Calculations</u>
                <div>Cut Volume (CY) = {item.cutVolume.toFixed(2)}</div>
                <div>Fill Volume (CY) = {item.fillVolume.toFixed(2)}</div>
                <div>Cut Rate = {item.cutRate.toFixed(2)}</div>
                <div>Field Area (acre) = {item.fieldArea.toFixed(2)}</div>
              </div>

              <div className="pb-1">
                <u>Buffer Calculations</u>
                <div>Cut Volume (CY) = {item.totalBufferCut.toFixed(2)}</div>
                <div>Fill Volume (CY) = {item.totalBufferFill.toFixed(2)}</div>
                <div>Buffer Area (acre) = {item.bufferArea.toFixed(2)}</div>
              </div>
            </div>
          </PrintReportElementContainer>
        ))}

      {/* Waterways  */}
      {vm.dataForDesignElement7.map((item, index) => (
        <WDTConstructionReport
          key={`WDTConstructionReport-${index}`}
          wdtContent={item}
          includeCrossSection={false}
          includeCheckout={false}
          project={vm.props.project}
        />
      ))}

      {vm.dataForDesignElement19.map((item, index) => (
        <WDTConstructionReport
          key={`WDTConstructionReport-${index}`}
          wdtContent={item}
          includeCrossSection={false}
          includeCheckout={false}
          project={vm.props.project}
        />
      ))}
      {/* Combined Terrace Construction Reports */}
      {(vm.dataForTerrace.length > 0 || vm.dataForUGOTerrace.length > 0 || (vm.isUGOSelected && vm.dataForUGO.length > 0)) && (
        <CombinedTerraceConstructionReport
          terraceReports={vm.dataForTerrace}
          ugoReports={vm.dataForUGOTerrace}
          project={props.project}
          isUGOSelected={vm.isUGOSelected}
          areIndividualTerracesSelected={vm.areIndividualTerracesSelected}
          ugoData={vm.dataForUGO}
          selectedElements={vm.getSelectedTerraceSystemElements()}
          planMapImage={vm.planMapImage}
          locationMapImage={vm.locationMapImage}
        />
      )}
    </div>
  );
};

export const PrintConstructionReports = observer(PrintDesignComponent);

const PrintReportElementContainer = observer(({ item, project, children }) => {
  let firstPageData: any = item.constructionReportPointList;
  let otherPagesData: any = [];
  if (item.designElementTypeId === 15) {
    const { firstPage, wholePages } = splitTable(item.constructionReportPointList, 16, 44);
    firstPageData = firstPage;
    otherPagesData = wholePages;
  }

  return (
    <>
      <OuterContainer item={item} project={project}>
        <h1 className="section-header">Structure Inputs/Properties - {item.name}</h1>
        <div className="report-content">
          <div className="report-structure-summary">
            <div className="content">
              <div className="image-container">
                {item.structureMapUrl && <img className="report-image" src={item.structureMapUrl} alt={item.name} />}
              </div>
              <div className="north-arrow-container">
                <img className="north-arrow" src={NorthArrow} alt="north-arrow" />
              </div>
              <div className="right-column py-2">{children}</div>
            </div>
          </div>

          <h2 className="section-header stakeout-table-header">Stakeout Table</h2>
          {firstPageData && firstPageData.length > 0 && <StakeoutTable items={firstPageData} />}
        </div>
      </OuterContainer>

      {otherPagesData && otherPagesData.length > 0
        ? otherPagesData.map((chunk, i) => (
          <OuterContainer key={`StakeoutTable-${i}`} item={item} project={project}>
            <StakeoutTable items={chunk} />
          </OuterContainer>
        ))
        : null}
    </>
  );
});

const OuterContainer = ({ item, project, children }) => (
  <>
    <div className="page-break"></div>
    <div className="container-outer-border">
      {children}
      <PrintReportFooter item={item} project={project} />
    </div>
  </>
);

const StakeoutTable = observer(({ items }) => (
  <table className="stakeout-table">
    <thead>
      <tr>
        <th>Point Name</th>
        <th>Description</th>
        <th>Easting (X)</th>
        <th>Northing (Y)</th>
        <th>Elevation (Z)</th>
        <th>Cut/Fill</th>
      </tr>
    </thead>
    <tbody>
      {items &&
        items.map((ray) => (
          <tr key={ray.pointName}>
            <td> {ray.pointName} </td>
            <td> {ray.description} </td>
            <td> {ray.x ? ray.x.toFixed(2) : ''} </td>
            <td> {ray.y ? ray.y.toFixed(2) : ''} </td>
            <td> {ray.z ? ray.z.toFixed(2) : ''} </td>
            <td>
              {' '}
              {ray.cutOrFill >= 0 ? `F ${ray.cutOrFill.toFixed(2)}` : `C ${Math.abs(ray.cutOrFill).toFixed(2)}`}{' '}
            </td>
          </tr>
        ))}
    </tbody>
  </table>
));

export const PrintReportFooter = ({ item, project }) => (
  <div className="print-report-footer">
    <div className="footer-outer-border"></div>

    <table className="usda-footer-table-class">
      <tbody>
        <tr>
          <td style={{ width: '20%' }}>
            <img id="usda-logo" src={usdaLogoDiagram} alt="usda logo" />
          </td>
          <td style={{ width: '20%', lineHeight: 1.2 }}>
            <div>
              <strong>
                {item.type}
                <br />
                Construction Report
                <br />
                {item.name}
              </strong>
            </div>
            {project.client}
            <br />
            {project.practice}
            <br />
            {project.county}, {project.state}
          </td>
          <td style={{ width: '30%' }}>
            <p style={{ textAlign: 'right', paddingRight: '10px' }}>Date</p>
            <table className="design-info">
              <tbody>
                <tr>
                  <th>Designed</th>
                  <td className="capitalize">{project.designedBy.split('@')[0].replace('.', ' ')}</td>
                  <td>{formatMonthDayYear(project.lastChangedDate)}</td>
                </tr>
                <tr>
                  <th>Drawn</th>
                  <td className="border-bottom"></td>
                  <td className="border-bottom"></td>
                </tr>
                <tr>
                  <th>Checked</th>
                  <td className="border-bottom"></td>
                  <td className="border-bottom"></td>
                </tr>
                <tr>
                  <th>Approved</th>
                  <td className="border-bottom"></td>
                  <td className="border-bottom"></td>
                </tr>
              </tbody>
            </table>
          </td>
          <td>
            <p>File Name</p>
            <p className="border-bottom">&nbsp;</p>
            <p>Drawing Name</p>
            <p>
              <strong>{project.projectTitle}</strong>
            </p>
            <p>{getMonthDayYear()}</p>
            <p>
              {/* Sheet {pageNumber + 1} of {totalPages} */}
              Sheet <span className="page-number"></span> of <span className="total-pages"></span>
            </p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);
